import Button from "../../../components/Button";
import ProgressBar from "../../../components/ProgressBar";
import { useNavigate } from "react-router-dom";
import TextArea from "../../../components/TextArea";
import AddIcon from "/public/images/icons/add.svg?react";
import { useState, useEffect } from "react";
import TextInput from "../../../components/TextInput";
import { useUserContext } from "../../../context/userContext";
import BinIcon from "/public/images/icons/trashRed.svg?react";
import SelectInput from "../../../components/SelectInput";
import { days, months, years } from "../../../utils/calendar"
import { PhoneNumberUtil } from 'google-libphonenumber';
import AdvancedPhoneInput from "../../../components/AdvancedPhoneInput";
const phoneUtil = PhoneNumberUtil.getInstance();

export default function GuestBandStep1({ band }) {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [phoneNumberErrors, setPhoneNumberErrors] = useState([]);
  const [localStorageDetails, setLocalStorageDetails] = useState(localStorage.getItem("activeGuestBandStep1") ? JSON.parse(localStorage.getItem("activeGuestBandStep1")) : null);
  const [emergencyContacts, setEmergencyContacts] = useState(localStorageDetails?.additionalICEContacts ? localStorageDetails?.additionalICEContacts : []);
  const [selectedDay, setSelectedDay] = useState(
    localStorageDetails?.participantDOB.split("-")[2]|| ''
  );

  const [selectedMonth, setSelectedMonth] = useState(
    localStorageDetails?.participantDOB.split("-")[1] || ''
  );

  const [selectedYear, setSelectedYear] = useState(
    localStorageDetails?.participantDOB.split("-")[0] || ''
  );

  const [participantDOB, setParticipantDOB] = useState(
    localStorageDetails?.participantDOB || ''
  );

  useEffect(() => {
    if (selectedDay && selectedMonth && selectedYear) {
      setParticipantDOB(`${selectedYear}-${selectedMonth}-${selectedDay}`);
    }
  }, [selectedDay, selectedMonth, selectedYear]);

  const validateForm = async (event) => {
    event.preventDefault();
    const formDetails = new FormData(event.currentTarget);
    const phoneErrors = [];

    try {
      const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(formDetails.get('bandICEPhoneNumber'));
      const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);
  
      if (!validPhoneNumber) {
        phoneErrors.push({ name: 'bandICEPhoneNumber', error: 'This is not a valid phone number.' });
      }
    } catch {
      phoneErrors.push({ name: 'bandICEPhoneNumber', error: 'This is not a valid phone number.' });
    }

    const structureFormDetails = {
      "participantName": formDetails.get('participantName'),
      "participantDOB": formDetails.get('participantDOB'),
      "bandPrimaryCondition": formDetails.get('bandPrimaryCondition'),
      "bandICEName": formDetails.get('bandICEName'),
      "bandICEPhoneNumber": formDetails.get('bandICEPhoneNumber'),
      "bandICERelation": formDetails.get('bandICERelation'),
      "participantNotes": formDetails.get('participantNotes'),
    };
    
    const additionalICEContacts = emergencyContacts?.map((_, index) => ({
      name: formDetails.get(`bandAdditionalICEName${index + 1}`),
      phoneNumber: formDetails.get(`bandAdditionalICEPhoneNumber${index + 1}`),
      relation: formDetails.get(`bandAdditionalICERelation${index + 1}`),
    }));

    additionalICEContacts.forEach((contact, index) => {
      try {
        const validateAdditionalPhoneNumber = phoneUtil.parseAndKeepRawInput(contact.phoneNumber);
        const validAdditionalPhoneNumber = phoneUtil.isValidNumber(validateAdditionalPhoneNumber);
  
        if (!validAdditionalPhoneNumber) {
          phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index + 1}`, error: 'This is not a valid phone number.' });
        }
      } catch {
        phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index + 1}`, error: 'This is not a valid phone number.' });
      }
    });

    // If there are any errors, set them in the state
    if (phoneErrors.length > 0) {
      setPhoneNumberErrors(phoneErrors);
      return false;  // Indicating that validation failed
    }

    structureFormDetails.additionalICEContacts = additionalICEContacts;
    localStorage.setItem("activeGuestBandStep1", JSON.stringify(structureFormDetails));

    navigate(`/guest-bands/${band?.BandID}/activate/2`, {state: {band: band}});
  };

  const addEmergencyContact = (e) => {
    e.preventDefault(); // Prevent form submission
    setEmergencyContacts([...emergencyContacts, {}]);
  };

  const removeEmergencyContact = (e, index) => {
    e.preventDefault(); // Prevent form submission
    setEmergencyContacts(emergencyContacts.filter((_, i) => i !== index));
  };

  const handlePhoneNumberChange = (value, inputName) => {
    try {
      const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(value);
      const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);

      if (validPhoneNumber) {
        // Clear error for this specific input if number is valid
        setPhoneNumberErrors(prev => prev.filter(error => error.name !== inputName));
      }
    } catch {
      // Do nothing if the number is invalid - keep existing error if any
    }
  };

  return (
    <div className="w-full min-h-svh flex flex-col justify-stretch gap-6 p-[24px] mb-[40px]">
      <ProgressBar step={1} overrideTotalSteps={4}/>
      <div>
        <span className="tracking-[0.25em] text-[12px] uppercase font-semibold">
          Step 1
        </span>
        <h2>Great, let&apos;s get your profile set up</h2>
      </div>
      <div className="border-dashed border-t border-[#E3E3E3]" />
      <form onSubmit={validateForm} className="space-y-4 flex flex-col justify-stretch mb-[40px]">
        <div>
          <TextInput
            label="Participant Name"
            name="participantName"
            required={true}
            defaultValue={localStorageDetails?.participantName}
          />
        </div>

        <div>
          <span className="text-sportsBlack font-medium text-base leading-[26px] mb-3 block">Participant Date of Birth</span>
          <div className="grid grid-cols-3 gap-[6px]">
            <SelectInput 
              label="Day"
              placeholder="Select"
              name="participantDOBDay"
              defaultValue={selectedDay}
              options={days?.map((day, index) => (<option key={day + '_' + index} value={day.value}>{day.label}</option>))}
              onChange={(e) => setSelectedDay(e.target.value)}
            />
            <SelectInput 
              label="Month"
              placeholder="Select"
              name="participantDOBMonth"
              defaultValue={selectedMonth}
              options={months?.map((month, index) => (<option key={month + '_' + index} value={month.value}>{month.label}</option>))}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
            <SelectInput 
              label="Year"
              placeholder="Select"
              name="participantDOBYear"
              defaultValue={selectedYear}
              options={years?.map((year, index) => (<option key={year + '_' + index} value={year.value}>{year.label}</option>))}
              onChange={(e) => setSelectedYear(e.target.value)}
            />
          </div>
          <input type="hidden" name="participantDOB" value={participantDOB} />
        </div>

        <div className="border-dashed border-t border-[#E3E3E3]" />

        <div>
          <TextInput
            label="Primary Medical Condition"
            name="bandPrimaryCondition"
            required={true}
            defaultValue={localStorageDetails?.bandPrimaryCondition}
          />
        </div>

        <div className="border-dashed border-t border-[#E3E3E3]" />

        <div>
          <TextInput
            label="Primary Emergency Contact Name (ICE)"
            name="bandICEName"
            defaultValue={localStorageDetails?.bandICEName}
            required={false}
          />
        </div>

        <p className="text-sportsGrey">
          We recommend letting your ICE contacts know that you have listed them
          in the Sports Guardian app.
        </p>

        <AdvancedPhoneInput 
          label="Primary Emergency Contact Number"
          inputName="bandICEPhoneNumber"
          defaultValue={localStorageDetails?.bandICEPhoneNumber || "+44"}
          required={true}
          error={phoneNumberErrors}
          onChange={(value) => handlePhoneNumberChange(value, 'bandICEPhoneNumber')}
        />

        <div>
          <TextInput
            label="Primary Emergency Contact Relationship"
            name="bandICERelation"
            required={false}
            defaultValue={localStorageDetails?.bandICERelation}
          />
        </div>

        {emergencyContacts.map((contact, index) => (
          <div className="flex flex-col gap-2" key={index}>
            <TextInput
              label={`Emergency Contact Name (${index + 1})`}
              name={`bandAdditionalICEName${index + 1}`}
              required={true}
              defaultValue={contact?.name}
            />

            <AdvancedPhoneInput 
              label={`Emergency Contact Number (${index + 1})`}
              inputName={`bandAdditionalICEPhoneNumber${index + 1}`}
              defaultValue={contact?.phoneNumber || "+44"}
              required={true}
              error={phoneNumberErrors}
              onChange={(value) => handlePhoneNumberChange(value, `bandAdditionalICEPhoneNumber${index + 1}`)}
            />

            <TextInput
              label={`Emergency Contact Relationship (${index + 1})`}
              name={`bandAdditionalICERelation${index + 1}`}
              required={true}
              defaultValue={contact?.relation}
            />

            <Button
              variant="tertiary"
              status="nobg"
              onClick={(e) => { removeEmergencyContact(e, index); return false; }}
              className="mt-2 mb-2 text-sportsRed"
            >
              <BinIcon className="w-[24px] [&>path]:stroke-sportsRed" />
              Remove
            </Button>
          </div>
        ))}

        {emergencyContacts.length < parseInt(band?.GuestBandICEContactLimit) && (
          <Button className="font-semibold" variant="tertiary" onClick={addEmergencyContact}>
            <AddIcon className="w-[15px]" />
            Add Additional ICE
          </Button>
        )}

        <div className="border-dashed border-t border-[#E3E3E3]" />
        
        <div>
          <TextArea 
            label="Notes"
            name="participantNotes"
            defaultValue={localStorageDetails?.participantNotes}
          />
        </div>

        <Button submit={true}>Continue</Button>
      </form>
    </div>
  );
}